import React, { useState, useEffect } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { Row, Space, Spinner, Button } from "@masterteam/mt-design-react";

import Table from "../../NewTable";
import WorkflowModal from "../WorkflowModal";

import api from "../../../providers/api";
import useIsAr from "../../../Hooks/useIsAr";
import { addToast, getLookupName } from "../../../helpers";

import { ReactComponent as InfoIcon } from "../../../assets/images/info-icon.svg";
import { ReactComponent as HistoryIcon } from "../../../assets/images/history.svg";
import { ReactComponent as ActionsIcon } from "../../../assets/images/actions.svg";

import "./style.scss";

const initialModalState = {
  isOpen: false,
  data: null,
};

const Archive = () => {
  const intl = useIntl();
  const [isAr] = useIsAr();
  const history = useHistory();
  const [workflowModal, setWorkflowModal] = useState(initialModalState);

  const [TaskCenterActions, BaseWorkflowTypes, WorkflowStatus] = useSelector(
    ({ theme }) => [
      theme.TaskCenterActions.item.Items.filter((e) => e.Value !== "All"),
      theme.BaseWorkflowTypes.item.Items,
      theme.WorkflowStatus.item.Items,
    ]
  );

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(true);

  const handleOnClick = (item) => {
    if (item.Level === 1) history.push(`/splus/kpi-details/${item?.LevelID}`);
  };

  const getData = async (newKey) => {
    try {
      setLoading(!newKey);
      setContentLoading(true);

      const res = await api.taskCenter.getArchive();

      setData(res?.Data || []);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error",
      });
    } finally {
      setLoading(false);
      setContentLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const myTasksColumns = [
    {
      key: "assignment-type",
      header: <FormattedMessage id="my-task.assignment-type" />,
      cell: (item) => {
        return item?.Action === 2
          ? getLookupName(TaskCenterActions, item?.Action.toString(), isAr)
          : getLookupName(
              BaseWorkflowTypes,
              item?.BaseWorkflowID.toString(),
              isAr
            );
      },
      width: "15%",
    },
    {
      key: "name",
      header: <FormattedMessage id="my-task.name" />,
      cell: (item) => (isAr ? item?.ArabicName : item?.EnglishName),
      width: "25%",
    },
    {
      key: "received-date",
      header: <FormattedMessage id="my-task.received-date" />,
      cell: (item) =>
        item.Created ? moment(item.Created).format("DD-MM-YYYY") : "---",
      width: "15%",
    },
    {
      key: "due-date",
      header: (
        <Space align="center" gap={0}>
          <FormattedMessage id="my-task.due-date" />

          <Button
            shape="round"
            Icon={InfoIcon}
            tooltip-position="top"
            tooltip={intl.formatMessage({ id: "my-task.due-date-tooltip" })}
          />
        </Space>
      ),
      cell: (item) =>
        item.DueDate ? moment(item.DueDate).format("DD-MM-YYYY") : "---",
      width: "15%",
    },
    {
      key: "workflow",
      header: <FormattedMessage id="my-task.workflow" />,
      cell: (item) => (
        <div className="icon-wrapper">
          <HistoryIcon
            onClick={(e) => {
              e.stopPropagation();
              setWorkflowModal({ isOpen: true, data: item.Request });
            }}
          />
        </div>
      ),
      width: "15%",
    },
    {
      key: "actions",
      header: <FormattedMessage id="my-task.actions" />,
      cell: (item) => (
        <div className="icon-wrapper">
          <ActionsIcon
            onClick={(e) => {
              e.stopPropagation();
              handleOnClick(item);
            }}
          />
        </div>
      ),
      width: "15%",
    },
  ];

  const myRequestsColumns = [
    {
      key: "request-type",
      header: <FormattedMessage id="my-requests.request-type" />,
      cell: (item) => {
        return item?.Action === 2
          ? getLookupName(TaskCenterActions, item?.Action.toString(), isAr)
          : getLookupName(
              BaseWorkflowTypes,
              item?.BaseWorkflowID.toString(),
              isAr
            );
      },
      width: "15%",
    },
    {
      key: "workFlow-type",
      header: <FormattedMessage id="my-task.workflow-type" />,
      cell: (item) => {
        return (
          <div>
            {item?.Action === 1
              ? "---"
              : getLookupName(
                  BaseWorkflowTypes,
                  item?.BaseWorkflowID.toString(),
                  isAr
                )}
          </div>
        );
      },
      width: "15%",
    },
    {
      key: "creation-date",
      header: <FormattedMessage id="my-requests.creation-date" />,
      cell: (item) =>
        item.Created ? moment(item.Created).format("DD-MM-YYYY") : "---",
      width: "20%",
    },
    {
      key: "creator",
      header: <FormattedMessage id="my-requests.creator" />,
      cell: (item) => item.CreatedBy?.DisplayName || "---",
      width: "20%",
    },

    {
      key: "workflow",
      header: <FormattedMessage id="my-requests.workflow" />,
      cell: (item) => (
        <div className="icon-wrapper">
          <HistoryIcon
            onClick={(e) => {
              e.stopPropagation();
              setWorkflowModal({ isOpen: true, data: item.Request });
            }}
          />
        </div>
      ),
      width: "15%",
    },
    {
      key: "status",
      header: <FormattedMessage id="my-requests.status" />,
      cell: (item) =>
        getLookupName(WorkflowStatus, item?.Status.toString(), isAr),
      width: "15%",
    },
  ];

  return loading ? (
    <Row justify="center" style={{ marginTop: "24px" }}>
      <Spinner size="large" />
    </Row>
  ) : (
    <div>
      {contentLoading ? (
        <Row justify="center" style={{ marginTop: "24px" }}>
          <Spinner size="large" />
        </Row>
      ) : (
        <Space direction="vertical" gap={64}>
          <Space direction="vertical" size="medium">
            <Row size="medium" align="center" className="table-title">
              <FormattedMessage id="archive.archived-tasks" />
              <hr />
            </Row>
            <Table
              compact
              data={data.ArchivedTasks || []}
              columns={myTasksColumns}
              differentRowsColor
              headerHasBackgroundColor
              onClickItem={handleOnClick}
            />
          </Space>

          <Space direction="vertical" size="medium">
            <Row size="medium" align="center" className="table-title">
              <FormattedMessage id="archive.archived-requests" />
              <hr />
            </Row>

            <Table
              compact
              data={data.ArchivedRequests || []}
              columns={myRequestsColumns}
              differentRowsColor
              headerHasBackgroundColor
              onClickItem={handleOnClick}
            />
          </Space>
        </Space>
      )}

      {workflowModal.isOpen && (
        <WorkflowModal
          data={workflowModal.data}
          onClose={() => setWorkflowModal(initialModalState)}
        />
      )}
    </div>
  );
};

export default Archive;
